import {
  VISIT_SOURCE_DOCX_MEDIA_TYPE,
  VISIT_SOURCE_MAX_BYTES,
  type VisitSourceCreateInput,
} from "@rosc/contracts";

// What the picker offers. HEIC/HEIF photos are converted to JPEG in the
// browser where it can decode them (Safari, iOS); iOS usually hands the page
// a JPEG already.
export const SOURCE_ACCEPT =
  "image/png,image/jpeg,image/webp,image/heic,image/heif,application/pdf";
// Fast attach (VITE_ASSISTANT_FAST_UPLOADS_ENABLED) also takes Word documents.
export const SOURCE_ACCEPT_FAST = `${SOURCE_ACCEPT},.docx,${VISIT_SOURCE_DOCX_MEDIA_TYPE}`;
// Photos are re-encoded below, so a larger original is fine.
export const SOURCE_IMAGE_INPUT_MAX = 25 * 1024 * 1024;
export const SOURCE_PDF_MAX = VISIT_SOURCE_MAX_BYTES;
// Long side in pixels: enough for small print on a lab report.
const IMAGE_MAX_SIDE = 2400;

export type SourceFileError =
  | "unsupported"
  | "too-large"
  | "unreadable-image"
  | "empty";
export class SourceFileProblem extends Error {
  constructor(readonly reason: SourceFileError) {
    super(reason);
  }
}

export type PreparedSource = Pick<
  VisitSourceCreateInput,
  "mediaType" | "data" | "checksumSha256"
> & { byteLength: number; kind: SourceFileKind };
export type SourceFileKind = "image" | "pdf" | "docx";

const isPdf = (file: File) =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name);
const isImage = (file: File) =>
  file.type.startsWith("image/") || /\.(heic|heif)$/i.test(file.name);

const isDocx = (file: File) =>
  file.type === VISIT_SOURCE_DOCX_MEDIA_TYPE || /\.docx$/i.test(file.name);

// Word documents only count with fast attach (`docx`).
export const sourceKindOf = (
  file: File,
  { docx = false }: { docx?: boolean } = {},
): SourceFileKind | null =>
  isPdf(file)
    ? "pdf"
    : docx && isDocx(file)
      ? "docx"
      : isImage(file)
        ? "image"
        : null;

// Checked before anything is read, so the clinician hears about it at once.
export const checkSourceFile = (
  file: File,
  options: { docx?: boolean } = {},
): SourceFileError | null => {
  const kind = sourceKindOf(file, options);
  if (!kind) return "unsupported";
  if (file.size === 0) return "empty";
  if (file.size > (kind === "image" ? SOURCE_IMAGE_INPUT_MAX : SOURCE_PDF_MAX))
    return "too-large";
  return null;
};

const toBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 0x8000)
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  return btoa(binary);
};
const sha256 = async (bytes: Uint8Array<ArrayBuffer>): Promise<string> =>
  toBase64(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)));
const readBytes = (blob: Blob): Promise<Uint8Array<ArrayBuffer>> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(new Uint8Array(reader.result as ArrayBuffer));
    };
    reader.onerror = () => {
      reject(new SourceFileProblem("unsupported"));
    };
    reader.readAsArrayBuffer(blob);
  });

// Draws the photo onto a canvas and saves it as JPEG. This also drops EXIF
// metadata (location, device) that can come with phone photos.
const reencodeImage = async (file: File): Promise<Blob> => {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new SourceFileProblem("unreadable-image");
  }
  const scale = Math.min(
    1,
    IMAGE_MAX_SIDE / Math.max(bitmap.width, bitmap.height),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new SourceFileProblem("unreadable-image");
  // White behind transparent screenshots, so text stays readable as JPEG.
  context.fillStyle = "#fff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", 0.88);
  });
  if (!blob) throw new SourceFileProblem("unreadable-image");
  return blob;
};

const MEDIA_TYPE = {
  pdf: "application/pdf",
  docx: VISIT_SOURCE_DOCX_MEDIA_TYPE,
  image: "image/jpeg",
} as const;

export const prepareSourceFile = async (
  file: File,
  options: { docx?: boolean } = {},
): Promise<PreparedSource> => {
  const problem = checkSourceFile(file, options);
  if (problem) throw new SourceFileProblem(problem);
  const kind = sourceKindOf(file, options) ?? "image";
  const bytes = await readBytes(
    kind === "image" ? await reencodeImage(file) : file,
  );
  if (bytes.length > VISIT_SOURCE_MAX_BYTES)
    throw new SourceFileProblem("too-large");
  const head = String.fromCharCode(...bytes.subarray(0, 5));
  if (
    (kind === "pdf" && head !== "%PDF-") ||
    (kind === "docx" && !head.startsWith("PK\u0003\u0004"))
  )
    throw new SourceFileProblem("unsupported");
  return {
    kind,
    mediaType: MEDIA_TYPE[kind],
    data: toBase64(bytes),
    checksumSha256: await sha256(bytes),
    byteLength: bytes.length,
  };
};

export const formatBytes = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
